import { visit } from "@hotwired/turbo";

// #STYLE_GUIDE: TypeScript interfaces use camelCase (props are transformed by turbo-mount)
interface AvatarAppearanceData {
  id: string;
  displayName?: string;
}

interface QuestionProps {
  id: string;
  avatarAppearance: AvatarAppearanceData | string;
  avatarAppearanceId: string;
  questionText: string;
  createdAt: string;
  updatedAt: string;
}

interface QuestionShowProps {
  question: QuestionProps;
}

// #STYLE_GUIDE: Show component displays single record details
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
export default function QuestionShow({ question }: QuestionShowProps) {
  const handleBack = () => {
    // Navigate back to the avatar appearance page
    visit(`/avatar_appearances/${question.avatarAppearanceId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Question Details</h1>
        <button onClick={handleBack} className="btn btn-outline">
          Back to Avatar Appearance
        </button>
      </div>

      {/* Details card */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">
                <span className="label-text font-semibold">ID</span>
              </label>
              <p className="text-base-content">{question.id}</p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">
                  Avatar appearance
                </span>
              </label>
              <p className="text-base-content">
                {(() => {
                  // Handle reference field display - show displayName if it's an object, otherwise show the value
                  const value = question.avatarAppearance;
                  if (
                    typeof value === "object" &&
                    value !== null &&
                    "displayName" in value
                  ) {
                    return value.displayName;
                  }
                  return value || "Not set";
                })()}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Question text</span>
              </label>
              <div className="text-base-content whitespace-pre-wrap">
                {question.questionText || "Not set"}
              </div>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Created At</span>
              </label>
              <p className="text-base-content">
                {new Date(question.createdAt).toLocaleString()}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Updated At</span>
              </label>
              <p className="text-base-content">
                {new Date(question.updatedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
